"use client"
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Breadcrumb } from "../../components/ui/breadcrumb";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useChampignons } from "../../hooks/api/champignons/index";
import { PageHeader } from "../../ui/layout/page-header";
import { ChampignonsDetail } from "./champignons-detail";
import { ChampignonsForm } from "./champignons-form";
import { ChampignonsList } from "./champignons-list";

enum View {
  LIST = "list",
  DETAIL = "detail",
  CREATE = "create",
  EDIT = "edit",
}

export function ChampignonsManagement() {
  const [currentView, setCurrentView] = useState<View>(View.LIST);
  const [selectedChampignonsId, setSelectedChampignonsId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCategory, showDeleted, sortBy]);

  const { data: champignonsData, isLoading, error, refetch } = useChampignons({
    page,
    limit,
    search: debouncedSearch || undefined,
    includeDeleted: showDeleted,
    sortBy,
  });

  const handleCreateClick = () => {
    setCurrentView(View.CREATE);
    setSelectedChampignonsId(null);
  };

  const handleEditClick = (id: number) => {
    setSelectedChampignonsId(id);
    setCurrentView(View.EDIT);
  };

  const handleViewClick = (id: number) => {
    setSelectedChampignonsId(id);
    setCurrentView(View.DETAIL);
  };

  const handleBackToList = () => {
    setCurrentView(View.LIST);
    setSelectedChampignonsId(null);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
    refetch();
  };

  const onPageChange = (page: number) => {
    setPage(page);
    refetch();
  };

  const onPageSizeChange = (size: number) => {
    setLimit(size);
    setPage(1);
    refetch();
  };

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
    setPage(1);
    refetch();
  };

  const handleShowDeletedChange = (show: boolean) => {
    setShowDeleted(show);
    refetch();
  };

  const renderView = () => {
    switch (currentView) {
      case View.CREATE:
        return <ChampignonsForm onCancel={handleBackToList} onSuccess={handleBackToList} />;

      case View.EDIT:
        return selectedChampignonsId ? (
          <ChampignonsForm
            key={`edit-champignons-${selectedChampignonsId}`}
            champignonsId={selectedChampignonsId}
            onCancel={handleBackToList}
            onSuccess={handleBackToList}
          />
        ) : null;

      case View.DETAIL:
        return selectedChampignonsId ? (
          <ChampignonsDetail
            champignonsId={selectedChampignonsId}
            onBack={handleBackToList}
            onEdit={() => handleEditClick(selectedChampignonsId)}
          />
        ) : null;

      case View.LIST:
      default:
        return (
          <ChampignonsList
            champignons={champignonsData?.data || []}
            isLoading={isLoading}
            error={error}
            onView={handleViewClick}
            onEdit={handleEditClick}
            page={page}
            limit={limit}
            pageCount={champignonsData?.meta.totalPages || 0}
            total={champignonsData?.meta.total || 0}
            sortBy={sortBy}
            onLimitChange={onPageSizeChange}
            onSearchChange={handleSearch}
            searchTerm={searchTerm}
            onPageChange={onPageChange}
            handleSortChange={handleSortChange}
            showDeleted={showDeleted}
            onShowDeletedChange={handleShowDeletedChange}
          />
        );
    }
  };

  return (
    <div className="space-y-3">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          {
            label: "Gestion",
            onClick: () => { if (currentView !== View.LIST) handleBackToList(); },
          },
          {
            label: "Champignons",
            onClick: () => { if (currentView !== View.LIST) handleBackToList(); },
          },
          ...(currentView === View.DETAIL && selectedChampignonsId
            ? [{ label: "Détail", current: true }]
            : []),
          ...(currentView === View.CREATE
            ? [{ label: "Créer", current: true }]
            : []),
          ...(currentView === View.EDIT && selectedChampignonsId
            ? [{ label: "Modifier", current: true }]
            : []),
        ]}
        showHomeIcon
        onHomeClick={() => { if (currentView !== View.LIST) handleBackToList(); }}
        className="mb-1"
      />

      {/* Page header + action button — list view only */}
      {currentView === View.LIST && (
        <div className="flex items-start justify-between gap-4">
          <PageHeader className="mb-0 space-y-1">
            <PageHeader.Title>Gestion des Champignons</PageHeader.Title>
            <PageHeader.Description>
              Gérez les champignons phytosanitaires et leur impact sur les essences.
            </PageHeader.Description>
          </PageHeader>
          <Button onClick={handleCreateClick} className="shrink-0">
            <Plus className="h-4 w-4 mr-1.5" />
            Créer un champignon
          </Button>
        </div>
      )}

      {/* Content card */}
      <Card className="p-3">
        {renderView()}
      </Card>
    </div>
  );
}

export { ChampignonsDetail } from './champignons-detail';
export { ChampignonsForm } from './champignons-form';
export { ChampignonsList } from './champignons-list';
